import React, { useEffect, useState } from "react";
import { C } from "../constants.js";
import { I } from "../icons.jsx";
import { API, apiFetch, cachedFetch, invalidateApiCache } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

export function ProvisionFoldersCard({ matterId, showNotification }) {
  const [provisioning, setProvisioning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [alreadyProvisioned, setAlreadyProvisioned] = useState(false);
  const [folderCount, setFolderCount] = useState(0);
  const [checking, setChecking] = useState(true);
  // Live progress from the SSE stream — null until the first event
  // arrives. Shape: {current, total, percent, label, stage}.
  const [progress, setProgress] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);

  useEffect(() => {
    setChecking(true);
    // Cached: this hits Clio's live folder list server-side, and now
    // that a provisioned matter renders nothing, we were paying a Clio
    // round trip on every Documents-tab open just to draw nothing.
    // Folders do not change on their own; a stale window only delays
    // the card's return in the rare case someone deletes them in Clio.
    cachedFetch(`/api/matters/${matterId}/folder-status`, 300_000)
      .then(data => {
        if (data.provisioned) {
          setAlreadyProvisioned(true);
          setFolderCount(data.folder_count);
        }
      })
      .catch(e => console.error("Folder status check failed:", e))
      .finally(() => setChecking(false));
  }, [matterId]);

  // While provisioning, tick a 1-Hz timer for the elapsed-time display.
  useEffect(() => {
    if (!provisioning) {
      setElapsedSec(0);
      return;
    }
    const start = Date.now();
    const id = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [provisioning]);

  const provision = async () => {
    if (!window.confirm("Create the standard document folder structure for this matter?")) return;
    setProvisioning(true);
    setError(null);
    setResult(null);
    setProgress(null);

    try {
      // Stream progress via SSE. fetch() + ReadableStream is used
      // because EventSource only supports GET. Each SSE message has
      // shape: `event: <name>\ndata: <json>\n\n`.
      const resp = await fetch(`${API}/api/matters/${matterId}/provision-folders/stream`, {
        method: "POST",
      });
      if (!resp.ok || !resp.body) {
        // Server didn't accept the stream — fall back to the legacy
        // non-streaming endpoint so the user still gets a result.
        const fallback = await apiFetch(`/api/matters/${matterId}/provision-folders`, { method: "POST" });
        setResult(fallback);
        showNotification(
          `Document structure created: ${fallback.folders_created} folders, ${fallback.documents_uploaded} documents`,
          "success",
        );
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse complete SSE messages out of the buffer (separated by \n\n)
        let sep;
        while ((sep = buffer.indexOf("\n\n")) !== -1) {
          const rawMsg = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);

          // Each message: "event: name\ndata: json"
          let evtName = "message";
          let dataStr = "";
          for (const line of rawMsg.split("\n")) {
            if (line.startsWith("event:")) evtName = line.slice(6).trim();
            else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
          }
          let data = {};
          try { data = dataStr ? JSON.parse(dataStr) : {}; } catch (e) { /* ignore */ }

          if (evtName === "progress") {
            setProgress(data);
          } else if (evtName === "complete") {
            setResult(data);
            setProgress({
              current: data.folders_created + data.documents_uploaded,
              total: data.folders_created + data.documents_uploaded,
              percent: 100,
              label: "Done",
              stage: "complete",
            });
            showNotification(
              `Document structure created: ${data.folders_created} folders, ${data.documents_uploaded} documents`,
              "success",
            );
            return;
          } else if (evtName === "already_provisioned") {
            setAlreadyProvisioned(true);
            return;
          } else if (evtName === "error") {
            throw new Error(data.message || "Failed to provision folders");
          }
        }
      }
    } catch (err) {
      const msg = err.message || "Failed to provision folders";
      if (msg.includes("already_provisioned") || msg.includes("already has")) {
        setAlreadyProvisioned(true);
      } else {
        setError(msg);
      }
    } finally {
      invalidateApiCache(`/api/matters/${matterId}/folder-status`);
      setProvisioning(false);
    }
  };

  // Show loading state while checking
  if (checking) {
    return React.createElement("div", {
      style: { background:"#fff", borderRadius:"12px", padding:"20px 28px", border:`1px solid ${C.warmGray}`, marginBottom:"20px" }
    },
      React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"8px" } },
        React.createElement("h2", {
          style: { fontSize:"13px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600 }
        }, "Document Structure"),
        React.createElement("span", { style: { fontSize:"12px", color:C.textLight } }, "Checking..."),
      ),
    );
  }

  // Card renders in three states:
  //   - Provisioning in progress — spinner + progress bar
  //   - Just finished in this session — the completed card, so the
  //     user sees the outcome of the thing they just ran
  //   - Not yet provisioned — active "Create Document Structure" button
  //
  // Substep 5-π.6: a matter provisioned on some earlier visit renders
  // nothing at all. This is a one-time setup step, and once it is done
  // the card was a permanent full-width slab on every matter's
  // Documents tab holding a disabled button — it offered no action
  // (re-provisioning was already blocked) and no information anyone
  // came looking for.
  //
  // Safe to hide because /folder-status recomputes `provisioned` from
  // Clio's live folder list on every load: if the folders are ever
  // deleted, the card comes back on its own.
  const done = alreadyProvisioned || !!result;
  const completedCount = (result && result.folders_created) || folderCount;

  if (alreadyProvisioned && !result && !provisioning) return null;

  return React.createElement("div", {
    style: { background:"#fff", borderRadius:"12px", padding:"20px 28px", border:`1px solid ${C.warmGray}`, marginBottom:"20px" }
  },
    React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"center" } },
      React.createElement("div", null,
        React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" } },
          React.createElement("h2", {
            style: { fontSize:"13px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600, margin: 0 }
          }, "Document Structure"),
          done && React.createElement("span", {
            style: {
              fontSize: "11px", fontWeight: 700, color: C.success,
              background: C.successBg, padding: "2px 8px", borderRadius: "9999px",
              letterSpacing: "0.04em",
            },
          }, "COMPLETE"),
        ),
        React.createElement("div", { style: { fontSize:"13px", color:C.textLight } },
          done
            ? `${completedCount || 13} folders already provisioned for this matter.`
            : "Create the standard 13-folder structure with templates",
        ),
      ),
      React.createElement("button", {
        onClick: provision,
        disabled: provisioning || done,
        title: done ? "Folders are already set up for this matter" : undefined,
        style: {
          padding:"8px 20px", borderRadius:"8px", border:"none",
          background: (provisioning || done) ? C.warmGray : C.navy,
          color: done ? C.textMuted : "#fff",
          fontSize:"13px", fontWeight:600,
          cursor: (provisioning || done) ? "not-allowed" : "pointer",
          opacity: done ? 0.6 : 1,
          display:"flex", alignItems:"center", gap:"6px",
        }
      },
        provisioning
          ? React.createElement(Spinner, { size:14, color:"#fff" })
          : (done ? I.check : I.folder),
        provisioning
          ? " Creating folders..."
          : (done ? " Already created" : " Create Document Structure"),
      ),
    ),
    // Progress bar — shown while provisioning is in flight. Shows
    // percent done, current step label, current/total counts, and
    // elapsed time so the user knows it's still working.
    provisioning && React.createElement("div", {
      style: { marginTop: "16px" },
    },
      React.createElement("div", {
        style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px", fontSize: "12px", color: C.textMuted },
      },
        React.createElement("span", null,
          progress
            ? `${progress.label || "Working..."} (${progress.current} of ${progress.total})`
            : "Starting...",
        ),
        React.createElement("span", { style: { fontFamily: "monospace" } },
          `${progress ? progress.percent : 0}% · ${elapsedSec}s`,
        ),
      ),
      React.createElement("div", {
        style: {
          height: "8px", width: "100%", background: C.warmGray,
          borderRadius: "9999px", overflow: "hidden",
        },
      },
        React.createElement("div", {
          style: {
            height: "100%",
            width: `${progress ? progress.percent : 3}%`,
            background: C.gold,
            transition: "width 0.3s ease",
            borderRadius: "9999px",
          },
        }),
      ),
      progress && progress.total > 0 && React.createElement("div", {
        style: { marginTop: "6px", fontSize: "11px", color: C.textLight },
      }, "This usually takes 30–60 seconds. Don't refresh the page."),
    ),
    error && React.createElement("div", {
      style: { marginTop:"10px", padding:"8px 12px", borderRadius:"6px", background:C.errorBg, color:C.error, fontSize:"13px" }
    }, error),
  );
}

